import { Box, SxProps, useTheme } from "@mui/material";
import { Chart as ChartJS, ChartOptions, LinearScale, LineController, LineElement } from "chart.js";
import { Line } from "react-chartjs-2";
import { LineGraphSkeleton } from "src/components";

ChartJS.register(LinearScale, LineElement, LineController);

interface IProps {
  data: { x: string; y: number | null }[];
  isLoading?: boolean;
  sx?: SxProps;
  minYValue?: number;
  maxYValue?: number;
  color?: string;
}

const SparkLine = (props: IProps) => {
  const theme = useTheme();

  let yValues = props.data.filter((p) => p.y !== null).map((p) => p.y!);

  const maxYValue = Math.max(...yValues);
  const minYValue = Math.min(...yValues);
  const calculatedMaxYValue = maxYValue + (maxYValue - minYValue) * 0.1;

  const calculatedMinYValue = minYValue - (maxYValue - minYValue) * 0.1;

  function getOptions(): ChartOptions<"line"> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      events: [],
      elements: {
        point: {
          radius: 0,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        xAxis: {
          display: false,
        },
        yAxis: {
          display: false,
          min: yValues.length > 1 ? (props.minYValue ?? calculatedMinYValue) : undefined,
          max: yValues.length > 1 ? (props.maxYValue ?? calculatedMaxYValue) : undefined,
        },
      },
    };
  }

  function getData() {
    return {
      datasets: [
        {
          label: "Dataset",
          type: "line" as const,
          data: props.data,
          backgroundColor: props.color ?? theme.palette.primary.main,
          borderColor: props.color ?? theme.palette.primary.main,
          borderWidth: 2,
          tension: 0.3,
        },
      ],
    };
  }

  return (
    <Box sx={{ ...props.sx, overflow: "hidden" }}>
      {props.isLoading ? (
        <LineGraphSkeleton lineCount={1} pointCount={12} />
      ) : (
        <Line options={getOptions()} data={getData()} />
      )}
    </Box>
  );
};

export { SparkLine };
